import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  Lock,
  Mail,
  RefreshCw,
  LogOut,
  ExternalLink,
  Inbox as InboxIcon,
  Newspaper,
} from "lucide-react";
import InsightsAdmin from "./InsightsAdmin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "l2m_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(() => !!localStorage.getItem(TOKEN_KEY));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("calls");
  const navigate = useNavigate();

  const login = async (e) => {
    e?.preventDefault?.();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify`, { token });
      localStorage.setItem(TOKEN_KEY, token);
      setAuthed(true);
      await load(token);
    } catch (err) {
      setError("Invalid token. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const load = async (tok = token) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/strategy-call`, {
        headers: { "X-Admin-Token": tok },
      });
      setRows(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
        setError("Session expired. Sign in again.");
      } else {
        setError("Could not load submissions.");
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setRows([]);
    navigate("/");
  };

  React.useEffect(() => {
    if (authed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authed) {
    return (
      <div data-testid="page-admin-login" className="min-h-screen flex items-center justify-center px-4 py-32">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-32 right-1/4 w-[520px] h-[520px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(216,181,109,0.14), transparent 70%)" }} />
          <div className="absolute -bottom-32 left-1/4 w-[520px] h-[520px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(79,209,197,0.12), transparent 70%)" }} />
          <div className="absolute inset-0 grid-bg opacity-25 mask-fade-y" />
        </div>
        <form onSubmit={login} className="w-full max-w-md rounded-3xl glass-strong p-8 sm:p-10">
          <div className="w-10 h-10 rounded-xl bg-white/[0.03] border border-white/10 inline-flex items-center justify-center text-gold">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <h1 className="mt-5 font-display text-3xl sm:text-4xl leading-tight text-mute-100">
            Linked2Marketing · admin
          </h1>
          <p className="mt-3 text-[14px] text-mute-300">Enter your admin token to manage strategy calls and the Insights hub.</p>
          <label className="mt-7 block text-sm text-mute-100">Admin token</label>
          <div className="mt-2 relative">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-mute-300" />
            <input
              type="password"
              autoFocus
              data-testid="admin-token-input"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="paste token"
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-[14px] text-mute-100 placeholder:text-mute-300 focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/30"
            />
          </div>
          {error && <p data-testid="admin-error" className="mt-3 text-[13px] text-red-300">{error}</p>}
          <button
            type="submit"
            disabled={loading || !token}
            data-testid="admin-login-submit"
            className="btn-primary mt-6 w-full justify-center disabled:opacity-50"
          >
            {loading ? "Verifying…" : "Sign in"}
          </button>
          <p className="mt-5 text-[12px] text-mute-300">
            Token is set by the <code className="text-mute-100">ADMIN_TOKEN</code> env variable on the backend.
          </p>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="page-admin" className="pt-32 sm:pt-36 pb-24">
      <div className="absolute inset-x-0 top-0 h-[420px] -z-10">
        <div className="absolute right-0 top-0 w-[560px] h-[560px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(216,181,109,0.12), transparent 70%)" }} />
        <div className="absolute inset-0 grid-bg opacity-20 mask-fade-y" />
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Tabs */}
        <div
          role="tablist"
          aria-label="Admin sections"
          className="flex items-center gap-2 mb-10"
        >
          <button
            role="tab"
            data-testid="admin-tab-calls"
            aria-selected={tab === "calls"}
            onClick={() => setTab("calls")}
            className={
              "inline-flex items-center gap-2 rounded-full px-4 py-2 text-[13px] tracking-[0.01em] border backdrop-blur-md transition-colors " +
              (tab === "calls"
                ? "border-gold/60 bg-gold/[0.10] text-gold"
                : "border-white/10 bg-white/[0.025] text-mute-100 hover:border-gold/40 hover:text-gold")
            }
          >
            <InboxIcon className="w-3.5 h-3.5" /> Strategy calls
          </button>
          <button
            role="tab"
            data-testid="admin-tab-insights"
            aria-selected={tab === "insights"}
            onClick={() => setTab("insights")}
            className={
              "inline-flex items-center gap-2 rounded-full px-4 py-2 text-[13px] tracking-[0.01em] border backdrop-blur-md transition-colors " +
              (tab === "insights"
                ? "border-gold/60 bg-gold/[0.10] text-gold"
                : "border-white/10 bg-white/[0.025] text-mute-100 hover:border-gold/40 hover:text-gold")
            }
          >
            <Newspaper className="w-3.5 h-3.5" /> Insights
          </button>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={logout} data-testid="admin-logout" className="btn-secondary">
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>

        {tab === "insights" ? (
          <InsightsAdmin
            token={token}
            onUnauthorized={() => {
              localStorage.removeItem(TOKEN_KEY);
              setAuthed(false);
            }}
          />
        ) : (
        <>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <span className="eyebrow text-gold">Strategy call inbox</span>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl tracking-tight text-mute-100">
              Submissions · {rows.length}
            </h1>
            <p className="mt-3 text-[14.5px] text-mute-300">
              Most recent first. Data lives in MongoDB (<code className="text-mute-100">strategy_calls</code> collection).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load()} data-testid="admin-refresh" className="btn-secondary" disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-6 rounded-xl border border-red-400/30 bg-red-500/[0.07] p-4 text-red-300 text-sm">
            {error}
          </div>
        )}

        <div className="mt-8 rounded-2xl glass overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[11px] uppercase tracking-[0.18em] text-mute-300 border-b border-white/5 bg-white/[0.02]">
            <div className="col-span-3">Name · business</div>
            <div className="col-span-3">Contact</div>
            <div className="col-span-2">Industry</div>
            <div className="col-span-2">Needs</div>
            <div className="col-span-2 text-right">When</div>
          </div>
          {rows.length === 0 && !loading && (
            <div data-testid="admin-empty" className="px-5 py-16 text-center text-mute-300">
              No submissions yet.
            </div>
          )}
          {rows.map((r) => (
            <details
              key={r.id}
              data-testid={`admin-row-${r.id}`}
              className="group border-b border-white/5 last:border-b-0 px-5 py-4 hover:bg-white/[0.02] transition-colors"
            >
              <summary className="cursor-pointer list-none md:grid md:grid-cols-12 md:gap-3 items-start flex flex-col gap-2">
                <div className="md:col-span-3">
                  <div className="text-[14.5px] text-mute-100">{r.name}</div>
                  <div className="text-[12.5px] text-mute-300">{r.business_name || "—"}</div>
                </div>
                <div className="md:col-span-3 text-[13px]">
                  <a href={`mailto:${r.email}`} className="text-teal hover:text-gold inline-flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" />
                    {r.email}
                  </a>
                  {r.phone && <div className="text-mute-300 mt-0.5">{r.phone}</div>}
                </div>
                <div className="md:col-span-2 text-[13px] text-mute-200">{r.industry || "—"}</div>
                <div className="md:col-span-2 text-[12.5px] text-mute-200">
                  {(r.needs || []).slice(0, 3).join(", ")}{(r.needs || []).length > 3 ? "…" : ""}
                </div>
                <div className="md:col-span-2 md:text-right text-[12px] text-mute-300 tabular-nums">
                  {new Date(r.created_at).toLocaleString()}
                </div>
              </summary>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-[13.5px] text-mute-200">
                <div className="rounded-lg bg-white/[0.02] border border-white/5 p-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-mute-300">Challenge</div>
                  <p className="mt-2 text-mute-100 whitespace-pre-wrap">{r.challenge || "—"}</p>
                </div>
                <div className="rounded-lg bg-white/[0.02] border border-white/5 p-4 space-y-1.5">
                  <Row k="Website" v={r.website_url ? <a className="text-teal hover:text-gold inline-flex items-center gap-1" href={r.website_url} target="_blank" rel="noreferrer">{r.website_url}<ExternalLink className="w-3 h-3" /></a> : "—"} />
                  <Row k="Preferred contact" v={r.preferred_contact || "—"} />
                  <Row k="All needs" v={(r.needs || []).join(", ") || "—"} />
                  <Row k="ID" v={<span className="text-mute-300 text-[11px]">{r.id}</span>} />
                </div>
              </div>
            </details>
          ))}
        </div>
        </>
        )}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-[12px] uppercase tracking-[0.16em] text-mute-300 flex-none">{k}</span>
      <span className="text-right text-[13px] text-mute-100">{v}</span>
    </div>
  );
}
